import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, AlertTriangle, Clock, Send, CheckCircle, Shield, RefreshCw, Info, Eye, Phone, Upload, X, Image, Heart, Share2, ExternalLink, Navigation, Bell } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Newsletter from '../components/Newsletter';
import { useLanguage } from '../context/LanguageContext';
import PushNotificationManager from '../components/PushNotificationManager';
import PWAInstallPrompt from '../components/PWAInstallPrompt';
import InstallAppButton from '../components/InstallAppButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const APP_URL = window.location.origin;

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom red alert marker
const alertIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#dc2626" width="36" height="36">
      <path d="M12 0C7.58 0 4 3.58 4 8c0 5.25 8 13 8 13s8-7.75 8-13c0-4.42-3.58-8-8-8zm0 11c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z"/>
    </svg>
  `),
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36],
});

// Twin Cities neighborhoods/areas
const neighborhoods = [
  // Minneapolis
  { id: 'downtown-mpls', name: 'Downtown Minneapolis', city: 'Minneapolis', lat: 44.9778, lng: -93.2650 },
  { id: 'north-mpls', name: 'North Minneapolis', city: 'Minneapolis', lat: 45.0094, lng: -93.2977 },
  { id: 'northeast-mpls', name: 'Northeast Minneapolis', city: 'Minneapolis', lat: 45.0027, lng: -93.2466 },
  { id: 'south-mpls', name: 'South Minneapolis', city: 'Minneapolis', lat: 44.9341, lng: -93.2590 },
  { id: 'uptown', name: 'Uptown', city: 'Minneapolis', lat: 44.9481, lng: -93.2985 },
  { id: 'phillips', name: 'Phillips', city: 'Minneapolis', lat: 44.9570, lng: -93.2617 },
  { id: 'powderhorn', name: 'Powderhorn', city: 'Minneapolis', lat: 44.9370, lng: -93.2390 },
  { id: 'little-earth', name: 'Little Earth', city: 'Minneapolis', lat: 44.9577, lng: -93.2458 },
  { id: 'cedar-riverside', name: 'Cedar-Riverside', city: 'Minneapolis', lat: 44.9692, lng: -93.2541 },
  { id: 'longfellow', name: 'Longfellow', city: 'Minneapolis', lat: 44.9398, lng: -93.2177 },
  { id: 'lake-street', name: 'Lake Street Corridor', city: 'Minneapolis', lat: 44.9485, lng: -93.2780 },
  { id: 'university-ave-mpls', name: 'University Ave (Minneapolis)', city: 'Minneapolis', lat: 44.9713, lng: -93.2312 },
  
  // Saint Paul
  { id: 'downtown-stp', name: 'Downtown Saint Paul', city: 'Saint Paul', lat: 44.9444, lng: -93.0930 },
  { id: 'west-side-stp', name: 'West Side', city: 'Saint Paul', lat: 44.9283, lng: -93.0892 },
  { id: 'east-side-stp', name: 'East Side', city: 'Saint Paul', lat: 44.9629, lng: -93.0506 },
  { id: 'frogtown', name: 'Frogtown', city: 'Saint Paul', lat: 44.9613, lng: -93.1180 },
  { id: 'payne-phalen', name: 'Payne-Phalen', city: 'Saint Paul', lat: 44.9766, lng: -93.0629 },
  { id: 'north-end-stp', name: 'North End', city: 'Saint Paul', lat: 44.9797, lng: -93.1052 },
  { id: 'university-ave-stp', name: 'University Ave (Saint Paul)', city: 'Saint Paul', lat: 44.9553, lng: -93.1570 },
  { id: 'midway', name: 'Midway', city: 'Saint Paul', lat: 44.9566, lng: -93.1657 },
  
  // Other Areas
  { id: 'bloomington', name: 'Bloomington', city: 'Metro', lat: 44.8408, lng: -93.2983 },
  { id: 'richfield', name: 'Richfield', city: 'Metro', lat: 44.8831, lng: -93.2669 },
  { id: 'brooklyn-park', name: 'Brooklyn Park', city: 'Metro', lat: 45.0941, lng: -93.3563 },
  { id: 'brooklyn-center', name: 'Brooklyn Center', city: 'Metro', lat: 45.0761, lng: -93.3327 },
  { id: 'other', name: 'Other Location', city: 'Other', lat: 44.9778, lng: -93.2650 }
];

const activityTypes = [
  { id: 'vehicle-presence', label: 'ICE/DHS Vehicles Present', icon: '🚗' },
  { id: 'officers-on-foot', label: 'Officers on Foot', icon: '👮' },
  { id: 'checkpoint', label: 'Checkpoint/Roadblock', icon: '🚧' },
  { id: 'arrest-activity', label: 'Arrest Activity', icon: '⚠️' },
  { id: 'surveillance', label: 'Apparent Surveillance', icon: '👁️' },
  { id: 'raid', label: 'Raid/Home Visit', icon: '🚨' },
  { id: 'other', label: 'Other Activity', icon: '📍' }
];

// Emergency Resources
const emergencyResources = [
  {
    name: 'ILCM Detention Intake Line',
    phone: '651-641-1011',
    hours: 'Mon-Thu 1-3 PM',
    description: 'For individuals detained by ICE or their families'
  },
  {
    name: 'ILCM Legal Intake',
    phone: '1-800-223-1368',
    hours: 'Mon 10AM-1PM, Tue 1-4PM, Thu 3-6PM',
    description: 'Free immigration legal assistance'
  },
  {
    name: 'Mid-Minnesota Legal Aid',
    phone: '612-332-1441',
    hours: 'Business hours',
    description: 'Immigration Law Project - deportation defense, citizenship'
  },
  {
    name: 'MN Refugee & Immigrant Helpline',
    phone: '1-800-814-4806',
    hours: 'Business hours',
    description: 'Statewide connections to legal help & services'
  },
  {
    name: 'United Way 211',
    phone: '211',
    hours: '24/7 Multilingual',
    description: 'Referrals to immigration resources'
  }
];

// Mental Health & Counseling Resources
const mentalHealthResources = [
  {
    name: '988 Suicide & Crisis Lifeline',
    phone: '988',
    hours: '24/7',
    description: 'Free crisis support for anyone in distress, including youth. Call or text.',
    type: 'crisis'
  },
  {
    name: 'Crisis Text Line',
    phone: 'Text 741741',
    hours: '24/7',
    description: 'Text-based crisis support for anxiety, depression, trauma',
    type: 'crisis'
  },
  {
    name: 'Minnesota Warmline',
    phone: '651-288-0400',
    hours: '24/7',
    description: 'Peer support for mental health navigation and emotional support',
    type: 'support'
  },
  {
    name: 'Empower Therapeutic Services',
    phone: '612-223-0373',
    hours: 'Business hours',
    description: 'Culturally sensitive therapy addressing racism, trauma. Serves immigrants & refugees.',
    address: '227 Colfax Ave N, Minneapolis',
    type: 'clinic'
  },
  {
    name: 'Healing Path Therapy Services',
    phone: '612-345-4850',
    hours: 'Business hours',
    description: 'BIPOC-focused, trauma-informed care. Art therapy, meditation, peer support for youth & adults.',
    address: '115 E Lake St #201, Minneapolis',
    type: 'clinic'
  }
];

const AlertMap = ({ autoOpenReport = false }) => {
  const { t } = useLanguage();
  const [reports, setReports] = useState([]);
  const [neighborhoodCounts, setNeighborhoodCounts] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showForm, setShowForm] = useState(autoOpenReport);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [isLocating, setIsLocating] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    neighborhood: '',
    location_detail: '',
    activity_type: '',
    description: '',
    image_url: ''
  });
  const [imagePreview, setImagePreview] = useState(null);

  // Auto-open report form if triggered from PWA shortcut
  useEffect(() => {
    if (autoOpenReport) {
      setShowForm(true);
    }
  }, [autoOpenReport]);

  // Geolocation function to find nearest neighborhood
  const detectLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userLat = position.coords.latitude;
        const userLng = position.coords.longitude;
        
        // Find nearest neighborhood
        let nearestNeighborhood = null;
        let minDistance = Infinity;
        
        neighborhoods.forEach(n => {
          const distance = Math.sqrt(
            Math.pow(n.lat - userLat, 2) + Math.pow(n.lng - userLng, 2)
          );
          if (distance < minDistance) {
            minDistance = distance;
            nearestNeighborhood = n;
          }
        });
        
        if (nearestNeighborhood) {
          setFormData(prev => ({ ...prev, neighborhood: nearestNeighborhood.id }));
          toast.success(`Location detected: ${nearestNeighborhood.name}`);
        }
        setIsLocating(false);
      },
      (error) => {
        setIsLocating(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error('Location permission denied. Please select manually.');
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error('Location unavailable. Please select manually.');
            break;
          case error.TIMEOUT:
            toast.error('Location request timed out. Please select manually.');
            break;
          default:
            toast.error('Unable to detect location. Please select manually.');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const fetchReports = useCallback(async () => {
    try {
      const [reportsRes, countsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/enforcement-reports`),
        axios.get(`${BACKEND_URL}/api/enforcement-reports/count`)
      ]);
      setReports(reportsRes.data);
      setNeighborhoodCounts(countsRes.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReports();
    // Auto-refresh every 2 minutes
    const interval = setInterval(fetchReports, 120000);
    return () => clearInterval(interval);
  }, [fetchReports]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload a JPEG, PNG, WebP, or GIF image.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setIsUploading(true);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/enforcement-reports/upload-image`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, image_url: `${BACKEND_URL}${response.data.image_url}` }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image. Please try again.');
      setImagePreview(null);
    } finally {
      setIsUploading(false);
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    setFormData(prev => ({ ...prev, image_url: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.neighborhood || !formData.activity_type) {
      toast.error('Please select a neighborhood and activity type');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/enforcement-reports`, formData);
      toast.success('Report submitted. Stay safe.');
      setFormData({ neighborhood: '', location_detail: '', activity_type: '', description: '', image_url: '' });
      setImagePreview(null);
      setShowForm(false);
      fetchReports();
    } catch (error) {
      toast.error('Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getNeighborhoodName = (id) => {
    const n = neighborhoods.find(n => n.id === id);
    return n ? n.name : id;
  };

  const getActivityLabel = (id) => {
    const a = activityTypes.find(a => a.id === id);
    return a ? a.label : id;
  };

  const getActivityIcon = (id) => {
    const a = activityTypes.find(a => a.id === id);
    return a ? a.icon : '📍';
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  // Get coordinates for a report based on neighborhood
  const getReportCoordinates = (report) => {
    const neighborhood = neighborhoods.find(n => n.id === report.neighborhood);
    if (neighborhood) {
      // Add slight random offset so multiple reports in same area don't overlap exactly
      const offset = 0.002;
      return [
        neighborhood.lat + (Math.random() - 0.5) * offset,
        neighborhood.lng + (Math.random() - 0.5) * offset
      ];
    }
    return [44.9778, -93.2650]; // Default to Minneapolis center
  };

  // Share functionality
  const shareAlertMap = async () => {
    const shareData = {
      title: 'XRU Community Alert Map',
      text: 'Track ICE/DHS activity in the Twin Cities. Report sightings to help keep our community safe.',
      url: `${APP_URL}/alert-map`
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        toast.success('Shared successfully!');
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyToClipboard();
        }
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(`${APP_URL}/alert-map`);
    toast.success('Link copied to clipboard!');
  };

  const totalReports = reports.length;
  const activeAreas = Object.keys(neighborhoodCounts).length;

  // Twin Cities center coordinates
  const mapCenter = [44.9537, -93.1658];
  const mapZoom = 11;

  return (
    <div className="bg-stone-50 pt-20">
      {/* PWA Install Prompt */}
      <PWAInstallPrompt />
      
      {/* Hero Section */}
      <section className="relative py-12 bg-gradient-to-b from-red-600 to-red-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium mb-4">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
              <span>{t('home.alertMapBanner.liveUpdates')}</span>
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {t('alertMap.heroTitle')}
            </h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-6">
              {t('alertMap.heroSubtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <button
                onClick={() => setShowForm(true)}
                className="px-8 py-4 bg-amber-500 hover:bg-amber-400 text-stone-900 font-semibold rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
                data-testid="report-activity-btn"
              >
                <AlertTriangle size={20} />
                {t('alertMap.reportActivity')}
              </button>
              <PushNotificationManager />
              <InstallAppButton />
              <button
                onClick={fetchReports}
                className="px-8 py-4 bg-white/20 hover:bg-white/30 text-white font-semibold rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
              >
                <RefreshCw size={20} className={isLoading ? 'animate-spin' : ''} />
                {t('alertMap.viewReports')}
              </button>
            </div>

            {/* Share Options */}
            <div className="flex flex-wrap justify-center gap-3 mb-4">
              <button
                onClick={shareAlertMap}
                className="px-6 py-3 bg-white/20 hover:bg-white/30 text-white font-semibold rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Share2 size={18} />
                Share
              </button>
            </div>

            {/* Quick Access Links */}
            <div className="flex flex-wrap justify-center gap-3 text-sm">
              <a
                href={`${APP_URL}/alert-map`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
              >
                <ExternalLink size={14} />
                <span>Direct Link: xru.org/alert-map</span>
              </a>
              <button
                onClick={copyToClipboard}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
              >
                <MapPin size={14} />
                <span>Copy Link</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Hotlines Banner */}
      <section className="py-4 bg-amber-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Phone className="text-stone-900" size={24} />
              <span className="font-bold text-stone-900">{t('alertMap.emergencyResources.legalHotlines')}</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
              <a href="tel:651-641-1011" className="flex items-center gap-2 px-3 py-1.5 bg-white/30 hover:bg-white/50 rounded-lg text-stone-900 font-medium transition-colors">
                <Phone size={14} />
                ILCM: 651-641-1011
              </a>
              <a href="tel:1-800-223-1368" className="flex items-center gap-2 px-3 py-1.5 bg-white/30 hover:bg-white/50 rounded-lg text-stone-900 font-medium transition-colors">
                <Phone size={14} />
                Legal Aid: 1-800-223-1368
              </a>
              <a href="tel:211" className="flex items-center gap-2 px-3 py-1.5 bg-white/30 hover:bg-white/50 rounded-lg text-stone-900 font-medium transition-colors">
                <Phone size={14} />
                211 (24/7)
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-4 bg-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Eye className="text-amber-400" size={18} />
                <span className="text-white font-medium">{totalReports} {t('alertMap.reportsToday')}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="text-red-400" size={18} />
                <span className="text-white font-medium">{activeAreas} {t('alertMap.activeAreas')}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 text-stone-400 text-sm">
              <Clock size={14} />
              <span>{t('alertMap.lastUpdate')}: {lastRefresh.toLocaleTimeString()}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-4 bg-amber-100 border-b border-amber-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-3">
            <Info className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="text-sm text-amber-800">
              <strong>{t('alertMap.knowYourRights.title')}:</strong> {t('alertMap.knowYourRights.rights.0')}. {t('alertMap.knowYourRights.rights.1')}. {t('alertMap.knowYourRights.rights.2')}.
              <Link to="/legal-help" className="ml-2 text-amber-700 hover:text-amber-900 underline font-medium">
                {t('alertMap.emergencyResources.viewAllResources')} →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Report Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-stone-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-stone-800">{t('alertMap.reportForm.title')}</h2>
                <button 
                  onClick={() => setShowForm(false)}
                  className="text-stone-400 hover:text-stone-600"
                >
                  <X size={24} />
                </button>
              </div>
              <p className="text-stone-500 text-sm mt-1">{t('alertMap.reportForm.disclaimer')}</p>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-stone-700">
                    {t('alertMap.reportForm.neighborhood')} *
                  </label>
                  <button
                    type="button"
                    onClick={detectLocation}
                    disabled={isLocating}
                    className="flex items-center gap-1 text-xs px-2 py-1 bg-teal-100 text-teal-700 hover:bg-teal-200 rounded-md transition-colors disabled:opacity-50"
                    data-testid="detect-location-btn"
                  >
                    <Navigation size={12} className={isLocating ? 'animate-spin' : ''} />
                    {isLocating ? 'Detecting...' : 'Use My Location'}
                  </button>
                </div>
                <select
                  value={formData.neighborhood}
                  onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg text-stone-800 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20"
                >
                  <option value="">{t('alertMap.reportForm.selectNeighborhood')}</option>
                  <optgroup label="Minneapolis">
                    {neighborhoods.filter(n => n.city === 'Minneapolis').map(n => (
                      <option key={n.id} value={n.id}>{n.name}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Saint Paul">
                    {neighborhoods.filter(n => n.city === 'Saint Paul').map(n => (
                      <option key={n.id} value={n.id}>{n.name}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Metro Area">
                    {neighborhoods.filter(n => n.city === 'Metro' || n.city === 'Other').map(n => (
                      <option key={n.id} value={n.id}>{n.name}</option>
                    ))}
                  </optgroup>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  {t('alertMap.reportForm.location')}
                </label>
                <input
                  type="text"
                  value={formData.location_detail}
                  onChange={(e) => setFormData({ ...formData, location_detail: e.target.value })}
                  placeholder="e.g., Near 38th & Chicago, by the school..."
                  className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Type of Activity *
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {activityTypes.map(type => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, activity_type: type.id })}
                      className={`p-3 rounded-lg border text-left text-sm transition-all ${
                        formData.activity_type === type.id
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-stone-200 bg-white text-stone-700 hover:border-stone-300'
                      }`}
                    >
                      <span className="mr-2">{type.icon}</span>
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Additional Details (optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  placeholder="Number of vehicles, officers, or other relevant details..."
                  className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 resize-none"
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Attach Photo (optional)
                </label>
                
                {imagePreview ? (
                  <div className="relative">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="w-full h-48 object-cover rounded-lg border border-stone-200"
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-stone-300 rounded-lg cursor-pointer hover:border-red-400 hover:bg-red-50/50 transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      {isUploading ? (
                        <div className="animate-spin text-red-500">
                          <Upload size={24} />
                        </div>
                      ) : (
                        <>
                          <Image className="text-stone-400 mb-2" size={24} />
                          <p className="text-sm text-stone-500">
                            <span className="font-medium text-red-500">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-stone-400 mt-1">PNG, JPG, WebP up to 5MB</p>
                        </>
                      )}
                    </div>
                    <input 
                      ref={fileInputRef}
                      type="file" 
                      className="hidden" 
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleImageUpload}
                      disabled={isUploading}
                    />
                  </label>
                )}
              </div>

              <div className="pt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 px-6 py-3 border border-stone-300 text-stone-700 font-medium rounded-lg hover:bg-stone-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || isUploading}
                  className="flex-1 px-6 py-3 bg-red-500 hover:bg-red-600 disabled:bg-stone-300 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <span className="animate-spin">⏳</span>
                  ) : (
                    <>
                      <Send size={18} />
                      Submit Report
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Interactive Map */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-stone-200 bg-stone-50 flex items-center justify-between">
                  <div>
                    <h2 className="font-semibold text-stone-800">Twin Cities Activity Map</h2>
                    <p className="text-sm text-stone-500">Red pins indicate reported enforcement activity</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 text-xs text-stone-500">
                      <div className="w-3 h-3 bg-red-500 rounded-full" />
                      <span>Active Report</span>
                    </div>
                  </div>
                </div>
                
                {/* Leaflet Map */}
                <div className="h-[500px] relative">
                  <MapContainer
                    center={mapCenter}
                    zoom={mapZoom}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={true}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    
                    {/* Report Markers */}
                    {reports.map(report => {
                      const coords = getReportCoordinates(report);
                      return (
                        <Marker
                          key={report.id}
                          position={coords}
                          icon={alertIcon}
                        >
                          <Popup>
                            <div className="min-w-[200px]">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xl">{getActivityIcon(report.activity_type)}</span>
                                <span className="font-bold text-red-600">{getActivityLabel(report.activity_type)}</span>
                              </div>
                              <p className="font-semibold text-stone-800">{getNeighborhoodName(report.neighborhood)}</p>
                              {report.location_detail && (
                                <p className="text-sm text-stone-600">{report.location_detail}</p>
                              )}
                              {report.description && (
                                <p className="text-sm text-stone-500 mt-1">{report.description}</p>
                              )}
                              <p className="text-xs text-stone-400 mt-2">Reported at {formatTime(report.reported_at)}</p>
                              {report.image_url && (
                                <img src={report.image_url} alt="Report" className="mt-2 w-full h-24 object-cover rounded" />
                              )}
                            </div>
                          </Popup>
                        </Marker>
                      );
                    })}
                  </MapContainer>
                </div>

                {/* Neighborhood Quick Reference */}
                <div className="p-4 border-t border-stone-200 bg-stone-50">
                  <p className="text-xs font-semibold text-stone-600 uppercase tracking-wide mb-2">Active Areas Today</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(neighborhoodCounts).map(([id, count]) => (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium"
                      >
                        <MapPin size={12} />
                        {getNeighborhoodName(id)} ({count})
                      </span>
                    ))}
                    {Object.keys(neighborhoodCounts).length === 0 && (
                      <span className="text-sm text-stone-500">No active areas - map is clear</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Reports Sidebar */}
            <div>
              <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden sticky top-24">
                <div className="p-4 border-b border-stone-200 bg-stone-50">
                  <h2 className="font-semibold text-stone-800">Recent Reports</h2>
                  <p className="text-sm text-stone-500">Today's activity feed</p>
                </div>
                
                <div className="max-h-[500px] overflow-y-auto">
                  {isLoading ? (
                    <div className="p-8 text-center text-stone-500">
                      <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                      Loading reports...
                    </div>
                  ) : reports.length === 0 ? (
                    <div className="p-8 text-center">
                      <CheckCircle className="text-teal-500 mx-auto mb-2" size={32} />
                      <p className="text-stone-600 font-medium">No reports today</p>
                      <p className="text-stone-500 text-sm mt-1">The map is clear. Stay vigilant.</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-stone-100">
                      {reports.map(report => (
                        <div key={report.id} className="p-4 hover:bg-stone-50 transition-colors">
                          <div className="flex items-start gap-3">
                            <div className="text-2xl">{getActivityIcon(report.activity_type)}</div>
                            <div className="flex-grow min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-stone-800 text-sm">
                                  {getNeighborhoodName(report.neighborhood)}
                                </span>
                              </div>
                              <p className="text-sm text-red-600 font-medium">
                                {getActivityLabel(report.activity_type)}
                              </p>
                              {report.location_detail && (
                                <p className="text-xs text-stone-500 mt-1">{report.location_detail}</p>
                              )}
                              {report.description && (
                                <p className="text-xs text-stone-600 mt-1">{report.description}</p>
                              )}
                              {report.image_url && (
                                <img 
                                  src={report.image_url} 
                                  alt="Report" 
                                  className="mt-2 w-full h-24 object-cover rounded-lg border border-stone-200"
                                />
                              )}
                              <p className="text-xs text-stone-400 mt-2 flex items-center gap-1">
                                <Clock size={12} />
                                {formatTime(report.reported_at)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="p-4 border-t border-stone-200 bg-stone-50">
                  <button
                    onClick={() => setShowForm(true)}
                    className="w-full px-4 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <AlertTriangle size={18} />
                    Report Activity
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Resources Section */}
      <section className="py-16 bg-red-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Phone className="text-white mx-auto mb-4" size={48} />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Immigration Emergency Resources
            </h2>
            <p className="text-white/80">Twin Cities area hotlines and legal aid</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {emergencyResources.map((resource, index) => (
              <div key={index} className="p-5 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                <h3 className="text-lg font-bold text-white mb-1">{resource.name}</h3>
                <a 
                  href={`tel:${resource.phone.replace(/[^0-9]/g, '')}`}
                  className="text-2xl font-bold text-amber-300 hover:text-amber-200 transition-colors"
                >
                  {resource.phone}
                </a>
                <p className="text-white/70 text-sm mt-1">{resource.hours}</p>
                <p className="text-white/90 text-sm mt-2">{resource.description}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-8 p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 text-center">
            <p className="text-white font-medium">
              If someone is being detained, call the ILCM Detention Line immediately: 
              <a href="tel:651-641-1011" className="text-amber-300 hover:text-amber-200 font-bold ml-2">651-641-1011</a>
            </p>
          </div>
        </div>
      </section>

      {/* Mental Health & Counseling Resources */}
      <section className="py-16 bg-gradient-to-b from-teal-600 to-teal-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Heart className="text-white mx-auto mb-4" size={48} />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Mental Health & Counseling
            </h2>
            <p className="text-white/80 max-w-2xl mx-auto">
              Immigration enforcement causes trauma for individuals, families, and entire communities. 
              You don't have to face it alone. These resources offer culturally sensitive support for our community members and youth.
            </p>
          </div>
          
          {/* Crisis Lines */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white/90 mb-4 text-center">24/7 Crisis Support</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {mentalHealthResources.filter(r => r.type === 'crisis' || r.type === 'support').map((resource, index) => (
                <div key={index} className="p-5 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 text-center">
                  <h4 className="text-lg font-bold text-white mb-2">{resource.name}</h4>
                  <a 
                    href={resource.phone.includes('Text') ? `sms:${resource.phone.replace(/[^0-9]/g, '')}` : `tel:${resource.phone.replace(/[^0-9]/g, '')}`}
                    className="text-2xl font-bold text-amber-300 hover:text-amber-200 transition-colors block"
                  >
                    {resource.phone}
                  </a>
                  <p className="text-white/70 text-sm mt-1">{resource.hours}</p>
                  <p className="text-white/90 text-sm mt-2">{resource.description}</p>
                </div>
              ))}
            </div>
          </div>
          
          {/* Counseling Clinics */}
          <div>
            <h3 className="text-lg font-semibold text-white/90 mb-4 text-center">Culturally Sensitive Counseling Services</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {mentalHealthResources.filter(r => r.type === 'clinic').map((resource, index) => (
                <div key={index} className="p-5 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                  <h4 className="text-lg font-bold text-white mb-1">{resource.name}</h4>
                  <a 
                    href={`tel:${resource.phone.replace(/[^0-9]/g, '')}`}
                    className="text-xl font-bold text-amber-300 hover:text-amber-200 transition-colors"
                  >
                    {resource.phone}
                  </a>
                  <p className="text-white/70 text-xs mt-1">{resource.address}</p>
                  <p className="text-white/90 text-sm mt-2">{resource.description}</p>
                </div>
              ))}
            </div>
          </div>
          
          <div className="mt-8 p-5 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-center md:text-left">
                <p className="text-white font-semibold">Supporting Our Youth</p>
                <p className="text-white/80 text-sm">
                  Young people affected by immigration enforcement may experience anxiety, fear, and trauma. 
                  These services offer age-appropriate, culturally sensitive support.
                </p>
              </div>
              <a 
                href="tel:988"
                className="flex-shrink-0 px-6 py-3 bg-amber-500 hover:bg-amber-400 text-stone-900 font-semibold rounded-lg transition-colors"
              >
                Youth Crisis: Call 988
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Safety Resources */}
      <section className="py-16 bg-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Community <span className="text-amber-400">Safety Resources</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-6 bg-stone-700/50 rounded-xl border border-stone-600">
              <Shield className="text-teal-400 mb-4" size={32} />
              <h3 className="text-lg font-semibold text-white mb-2">Know Your Rights</h3>
              <p className="text-stone-300 text-sm mb-4">
                You have constitutional rights regardless of immigration status. Learn what to do if approached by ICE.
              </p>
              <a 
                href="https://www.aclu.org/know-your-rights/immigrants-rights" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-teal-400 hover:text-teal-300 text-sm font-medium"
              >
                ACLU Rights Guide →
              </a>
            </div>
            
            <div className="p-6 bg-stone-700/50 rounded-xl border border-stone-600">
              <AlertTriangle className="text-amber-400 mb-4" size={32} />
              <h3 className="text-lg font-semibold text-white mb-2">Report Violations</h3>
              <p className="text-stone-300 text-sm mb-4">
                Document civil and human rights violations and report them to XRU. We investigate and advocate for justice.
              </p>
              <Link to="/contact" className="text-teal-400 hover:text-teal-300 text-sm font-medium">
                Report to XRU →
              </Link>
            </div>
            
            <div className="p-6 bg-stone-700/50 rounded-xl border border-stone-600">
              <MapPin className="text-red-400 mb-4" size={32} />
              <h3 className="text-lg font-semibold text-white mb-2">Stay Informed</h3>
              <p className="text-stone-300 text-sm mb-4">
                Follow XRU for updates on community safety, know-your-rights trainings, and rapid response alerts.
              </p>
              <Link to="/news" className="text-teal-400 hover:text-teal-300 text-sm font-medium">
                Latest News →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <Newsletter />
    </div>
  );
};

export default AlertMap;
